// ClothesManagement.jsx
import React, { useState, useEffect, useCallback } from "react";
import * as api from "../services/api";
import Card from "../components/Card";
import Spinner from "../components/Spinner";
import Modal from "../components/Modal";
import Button from "../components/Button";
import Input from "../components/Input";
import { toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import {
  PlusIcon,
  PencilSquareIcon,
  ArrowUpOnSquareIcon,
  TrashIcon,
  ArchiveBoxXMarkIcon,
  XCircleIcon,
} from "@heroicons/react/24/outline";

const CLASS_ORDER = ["الروضة", "الابتدائي", "الإعدادي"];

const CLASS_ITEMS_MAP = {
  "الروضة": ["جاكيت", "مريول"],
  "الابتدائي": ["جاكيت", "قميص", "بنطال"],
  "الإعدادي": ["قميص"]
};

const StockCard = ({ stock, onEdit, onExport, onDelete }) => (
  <div className="bg-white rounded-xl shadow-md overflow-hidden ring-1 ring-slate-200 transition-all hover:shadow-lg hover:-translate-y-1">
    <div className="p-4 bg-slate-50 border-b flex justify-between items-center">
      <h3 className="text-xl font-bold text-slate-900">{`المخزون - ${stock.classState}`}</h3>
      <div className="flex gap-1">
        <Button variant="ghost" size="icon" onClick={onEdit} title="إضافة كميات">
          <PencilSquareIcon className="h-6 w-6 text-blue-500 hover:text-blue-700" />
        </Button>
        <Button variant="ghost" size="icon" onClick={onExport} title="إخراج كميات">
          <ArrowUpOnSquareIcon className="h-6 w-6 text-yellow-500 hover:text-yellow-700" />
        </Button>
        <Button variant="ghost" size="icon" onClick={onDelete} title="حذف المخزون">
          <TrashIcon className="h-6 w-6 text-red-500 hover:text-red-700" />
        </Button>
      </div>
    </div>
    <div className="p-4">
      <table className="w-full text-md">
        <thead>
          <tr className="border-b text-lg text-slate-800">
            <th className="py-2 px-3 text-right font-semibold">النوع</th>
            <th className="py-2 px-3 text-center font-semibold">الكمية المتوفرة</th>
          </tr>
        </thead>
        <tbody>
          {stock.items && stock.items.length > 0 ? (
            stock.items.map((item, i) => (
              <tr key={i} className="border-b last:border-none hover:bg-slate-50">
                <td className="py-3 px-3 text-lg text-slate-800">{item.type}</td>
                <td className="py-3 px-3 text-center font-mono font-bold text-lg text-blue-700">{item.quantity}</td>
              </tr>
            ))
          ) : (
            <tr>
              <td colSpan="2" className="py-4 text-center text-slate-500 italic">لا توجد أصناف في هذا المخزون</td>
            </tr>
          )}
        </tbody>
      </table>
    </div>
  </div>
);

const EmptyState = ({ onAdd }) => (
  <div className="text-center py-16 px-6 border-2 border-dashed border-gray-300 rounded-lg">
    <ArchiveBoxXMarkIcon className="mx-auto h-12 w-12 text-gray-400" />
    <h3 className="mt-2 text-lg font-medium text-gray-900">لا يوجد مخزون مسجل</h3>
    <p className="mt-1 text-sm text-gray-500">ابدأ بإضافة أول مخزون ملابس للمدرسة.</p>
    <div className="mt-6">
      <Button onClick={onAdd} className="bg-green-600 hover:bg-green-700 flex items-center gap-2 mx-auto">
        <PlusIcon className="h-5 w-5" />
        إضافة مخزون جديد
      </Button>
    </div>
  </div>
);

const ClothesManagement = () => {
  const [stocks, setStocks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [isAddModalOpen, setIsAddModalOpen] = useState(false);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [isExportModalOpen, setIsExportModalOpen] = useState(false);
  const [classState, setClassState] = useState("");
  // items: controlled inputs, keep quantity as string for easy typing
  const [items, setItems] = useState([{ type: "", quantity: "" }]);
  const [selectedStockId, setSelectedStockId] = useState(null);

  const fetchStocks = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const { data } = await api.getClothesStock();
      setStocks(Array.isArray(data) ? data : []);
    } catch (err) {
      console.error(err);
      setError("فشل في جلب بيانات المخزون");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchStocks();
  }, [fetchStocks]);

  const resetForm = () => {
    setClassState("");
    setItems([{ type: "", quantity: "" }]);
    setSelectedStockId(null);
  };

  const handleItemChange = (index, field, value) => {
    setItems(prev => {
      const copy = [...prev];
      copy[index] = { ...copy[index], [field]: value };
      return copy;
    });
  };

  const handleAddItemField = () => setItems(prev => [...prev, { type: "", quantity: "" }]);

  const handleRemoveItemField = (index) => {
    if (items.length <= 1) {
      toast.info("يجب أن يوجد نوع واحد على الأقل.");
      return;
    }
    setItems(prev => prev.filter((_, i) => i !== index));
  };

  // PRE-FILL Add modal items by class selection (based on CLASS_ITEMS_MAP)
  const handleClassSelectionForAdd = (e) => {
    const cls = e.target.value;
    setClassState(cls);
    if (cls && CLASS_ITEMS_MAP[cls]) {
      const pre = CLASS_ITEMS_MAP[cls].map(type => ({ type, quantity: "" }));
      setItems(pre.length ? pre : [{ type: "", quantity: "" }]);
    } else {
      setItems([{ type: "", quantity: "" }]);
    }
  };

  // When selecting a stock to edit or export, prefill the types from server stock and clear quantities
  useEffect(() => {
    if (!selectedStockId) return;
    const stock = stocks.find(s => s._id === selectedStockId);
    if (!stock) return;
    const prefilled = (stock.items || []).map(it => ({ type: it.type || "", quantity: "" }));
    setClassState(stock.classState || "");
    setItems(prefilled.length ? prefilled : [{ type: "", quantity: "" }]);
  }, [selectedStockId, stocks]);

  // ---------- API Handlers ----------

  // Add new stock (create)
const handleAddStock = async (e) => {
  e.preventDefault();
  const payload = {
    classState,
    items: items.map(it => ({
      type: it.type,
      quantity: Number(it.quantity) || 0 // ✅ empty → 0
    }))
  };

  try {
    await api.addClothesStock(payload);
    toast.success("✅ تم إضافة المخزون بنجاح");
    setIsAddModalOpen(false);
    resetForm();
    await fetchStocks();
  } catch (err) {
    console.error(err);
    toast.error(err?.response?.data?.error || "فشل في إضافة المخزون");
  }
};

  // Edit: add quantities to existing stock
  // Important: we send only items that have quantity > 0, and quantities as numbers (deltas)
  const handleEditStock = async (e) => {
    e.preventDefault();
    if (!selectedStockId) {
      toast.error("لم يتم تحديد المخزون للتعديل");
      return;
    }

    const itemsToAdd = items
      .map(it => ({ type: it.type, quantity: Number(it.quantity) || 0 }))
      .filter(it => Number(it.quantity) > 0);

    if (itemsToAdd.length === 0) {
      toast.info("الرجاء إدخال كمية واحدة على الأقل للإضافة.");
      return;
    }

    try {
      // API assumed endpoint: addQuantityToClothes(stockId, { items: [...] }) - adjust if your API uses different key
      await api.addQuantityToClothes(selectedStockId, { items: itemsToAdd });
      toast.success("✅ تم تعديل الكميات بنجاح");
      setIsEditModalOpen(false);
      resetForm();
      await fetchStocks();
    } catch (err) {
      console.error(err);
      toast.error(err?.response?.data?.errors?.join?.(", ") || "فشل في التعديل");
    }
  };

  // Export: allow partial exports (user can leave some blank). Send only items with quantity > 0
  const handleExportStock = async (e) => {
    e.preventDefault();
    if (!selectedStockId) {
      toast.error("لم يتم تحديد المخزون للإخراج");
      return;
    }

    const itemsToExport = items
      .map(it => ({ type: it.type, quantity: Number(it.quantity) || 0 }))
      .filter(it => Number(it.quantity) > 0);

    if (itemsToExport.length === 0) {
      toast.info("الرجاء إدخال كمية واحدة على الأقل للإخراج.");
      return;
    }

    try {
      await api.exportClothes(selectedStockId, { itemsToExport });
      toast.success("✅ تم إخراج الملابس بنجاح");
      setIsExportModalOpen(false);
      resetForm();
      await fetchStocks();
    } catch (err) {
      console.error(err);
      toast.error(err?.response?.data?.errors?.join?.(", ") || "فشل في الإخراج");
    }
  };

  const handleDeleteStock = async (stockId) => {
    if (!window.confirm("هل أنت متأكد من حذف هذا المخزون؟ سيتم حذف جميع الأصناف والكميات المرتبطة به بشكل نهائي.")) return;
    try {
      await api.deleteClothesStock(stockId);
      toast.success("🗑️ تم حذف المخزون بنجاح");
      await fetchStocks();
    } catch (err) {
      console.error(err);
      toast.error("فشل في حذف المخزون");
    }
  };

  // ---------- Render ----------
  return (
    <div className="space-y-6">
      {/* Toast container (remove from here if you already have one at app root) */}

      <Card title="إدارة مخزون الملابس" titleClass="text-2xl font-bold text-slate-800">
        <div className="flex flex-col md:flex-row justify-between items-start md:items-center mb-6 gap-4">
          <p className="text-md text-slate-800">عرض وتعديل وإضافة مخزون الملابس للمراحل الدراسية المختلفة.</p>
          <Button onClick={() => { resetForm(); setIsAddModalOpen(true); }} className="bg-green-600 hover:bg-green-700 flex items-center gap-2 w-full md:w-auto">
            <PlusIcon className="h-5 w-5" />
            إضافة مخزون جديد
          </Button>
        </div>

        {loading && <div className="flex justify-center p-10"><Spinner /></div>}
        {!loading && error && <p className="text-red-500 text-center py-10">{error}</p>}
        {!loading && !error && (
          stocks.length > 0 ? (
            <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-6">
              {[...stocks]
                .sort((a, b) => CLASS_ORDER.indexOf(a.classState) - CLASS_ORDER.indexOf(b.classState))
                .map(stock => (
                  <StockCard
                    key={stock._id}
                    stock={stock}
                    // do NOT call resetForm() before setSelectedStockId so prefill effect works
                    onEdit={() => { setSelectedStockId(stock._id); setIsEditModalOpen(true); }}
                    onExport={() => { setSelectedStockId(stock._id); setIsExportModalOpen(true); }}
                    onDelete={() => handleDeleteStock(stock._id)}
                  />
                ))}
            </div>
          ) : <EmptyState onAdd={() => { resetForm(); setIsAddModalOpen(true); }} />
        )}
      </Card>

      {/* Add Stock Modal */}
      <Modal isOpen={isAddModalOpen} onClose={() => setIsAddModalOpen(false)} title="➕ إضافة مخزون جديد">
        <form onSubmit={handleAddStock} className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">المرحلة الدراسية</label>
            <select value={classState} onChange={handleClassSelectionForAdd} required className="border-gray-300 focus:border-indigo-500 focus:ring-indigo-500 rounded-md shadow-sm w-full p-2">
              <option value="">اختر المرحلة</option>
              <option value="الروضة">الروضة</option>
              <option value="الابتدائي">الابتدائي</option>
              <option value="الإعدادي">الإعدادي</option>
            </select>
            <p className="text-xs text-gray-500 mt-1">سيتم ملء الأصناف تلقائيًا حسب المرحلة. يمكنك تعديلها قبل الحفظ.</p>
          </div>

          <div className="space-y-3">
            {items.map((item, i) => (
              <div key={i} className="flex gap-2 items-end">
                <Input label="النوع" value={item.type} onChange={(e) => handleItemChange(i, "type", e.target.value)} required />
                <Input label="الكمية"type="number" value={item.quantity}onChange={(e) => handleItemChange(i, "quantity", e.target.value)}placeholder="0"/>
                <button type="button" onClick={() => handleRemoveItemField(i)} className="mb-1 text-gray-400 hover:text-red-500 transition-colors" aria-label="Remove Item"><XCircleIcon className="h-6 w-6" /></button>
              </div>
            ))}
          </div>

          <Button type="button" onClick={handleAddItemField} variant="ghost" className="text-blue-600 flex items-center gap-1"><PlusIcon className="h-4 w-4" /> إضافة نوع آخر</Button>

          <div className="flex justify-end gap-2 pt-4 border-t">
            <Button type="button" variant="secondary" onClick={() => setIsAddModalOpen(false)}>إلغاء</Button>
            <Button type="submit" className="bg-green-600 hover:bg-green-700">حفظ</Button>
          </div>
        </form>
      </Modal>

      {/* Edit Quantities Modal */}
      <Modal isOpen={isEditModalOpen} onClose={() => setIsEditModalOpen(false)} title="✏️ تعديل كميات">
        <form onSubmit={handleEditStock} className="space-y-4">
          <p className="text-sm text-gray-600">أدخل الأصناف والكميات التي تريد إضافتها إلى المخزون الحالي. اترك الحقول الفارغة للأصناف التي لا تريد تغييرها.</p>
          <div className="space-y-3">
            {items.map((item, i) => (
              <div key={i} className="flex gap-2 items-end">
                <Input label="النوع" value={item.type} onChange={(e) => handleItemChange(i, "type", e.target.value)} required />
                <Input label="الكمية المضافة" type="number" value={item.quantity} onChange={(e) => handleItemChange(i, "quantity", e.target.value)} />
                <button type="button" onClick={() => handleRemoveItemField(i)} className="mb-1 text-gray-400 hover:text-red-500 transition-colors" aria-label="Remove Item"><XCircleIcon className="h-6 w-6" /></button>
              </div>
            ))}
          </div>
          <Button type="button" onClick={handleAddItemField} variant="ghost" className="text-blue-600 flex items-center gap-1"><PlusIcon className="h-4 w-4" /> إضافة نوع آخر</Button>
          <div className="flex justify-end gap-2 pt-4 border-t">
            <Button type="button" variant="secondary" onClick={() => setIsEditModalOpen(false)}>إلغاء</Button>
            <Button type="submit" className="bg-blue-600 hover:bg-blue-700">حفظ التعديلات</Button>
          </div>
        </form>
      </Modal>

      {/* Export Modal */}
      <Modal isOpen={isExportModalOpen} onClose={() => setIsExportModalOpen(false)} title="📦 إخراج ملابس">
        <form onSubmit={handleExportStock} className="space-y-4">
          <p className="text-sm text-gray-600">أدخل الأصناف والكميات التي تريد إخراجها من المخزون. اترك الحقول الفارغة للصنف الذي لا تريد إخراجه.</p>
          <div className="space-y-3">
            {items.map((item, i) => (
              <div key={i} className="flex gap-2 items-end">
                <Input label="النوع" value={item.type} onChange={(e) => handleItemChange(i, "type", e.target.value)} required />
                {/* quantity is optional here */}
                <Input label="الكمية المخرجة" type="number" value={item.quantity} onChange={(e) => handleItemChange(i, "quantity", e.target.value)} />
                <button type="button" onClick={() => handleRemoveItemField(i)} className="mb-1 text-gray-400 hover:text-red-500 transition-colors" aria-label="Remove Item"><XCircleIcon className="h-6 w-6" /></button>
              </div>
            ))}
          </div>
          <Button type="button" onClick={handleAddItemField} variant="ghost" className="text-blue-600 flex items-center gap-1"><PlusIcon className="h-4 w-4" /> إضافة نوع آخر</Button>
          <div className="flex justify-end gap-2 pt-4 border-t">
            <Button type="button" variant="secondary" onClick={() => setIsExportModalOpen(false)}>إلغاء</Button>
            <Button type="submit" className="bg-yellow-600 hover:bg-yellow-700">تأكيد الإخراج</Button>
          </div>
        </form>
      </Modal>
    </div>
  );
};

export default ClothesManagement;
